import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { pluginApi, serverApi, verificationApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';

export function VerificationPlugin({ serverId }: { serverId: string }) {
  const { t } = useI18n();
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['servers', serverId, 'roles'],
    queryFn: () => serverApi.getRoles(serverId),
  });

  const web3Plugin = (plugins as any)?.find((p: any) => p.pluginId === 'web3-gating');
  const isEnabled = web3Plugin?.enabled ?? false;

  const {
    data: panelStatus,
    isLoading: panelStatusLoading,
    refetch: refetchPanelStatus,
  } = useQuery({
    queryKey: ['servers', serverId, 'verification', 'panel-status'],
    queryFn: () => verificationApi.getPanelStatus(serverId),
    enabled: isEnabled,
  });

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'web3-gating') : pluginApi.disablePlugin(serverId, 'web3-gating'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'web3-gating', config),
    {
      onSuccess: () => toast.success(t('verification.savedToast')),
      invalidateQueries: [['servers', serverId, 'plugins']]
    }
  );

  const { mutate: spawnVerificationPanel, isPending: spawningPanel } = useApiMutation(
    () => verificationApi.spawnPanel(serverId, config.verificationChannelId || undefined),
    {
      onSuccess: () => {
        toast.success(t('verification.publishedToast'));
        refetchPanelStatus();
      },
      onError: (error: any) => toast.error(error?.message || t('verification.publishError')),
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    verificationChannelId: '',
    verifiedRoleId: '',
    unverifiedRoleId: '',
    captchaEnabled: true,
    captchaProvider: 'discord-button',
    maxAttempts: 3,
    timeoutMinutes: 15,
    autoKickUnverified: false,
    methodButton: true,
    methodCaptcha: true,
    methodEmail: false,
    methodOAuthDiscord: false,
  });

  useEffect(() => {
    if (web3Plugin?.config) {
      setConfig((prev: any) => ({ ...prev, ...web3Plugin.config }));
    }
  }, [web3Plugin]);

  if (!web3Plugin) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Shield className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <p>{t('verification.unavailable')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-emerald-500/20 rounded-xl">
            <Shield className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">{t('verification.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('verification.subtitle')}</p>
          </div>
        </div>
        <Switch checked={isEnabled} onCheckedChange={(enabled) => togglePlugin({ enabled })} />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 space-y-4">
            <div className="rounded-lg bg-white/5 p-4 text-sm space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-medium">{t('verification.embedStatus')}</span>{' '}
                  {panelStatusLoading
                    ? t('verification.loading')
                    : panelStatus?.spawned
                    ? t('verification.spawned')
                    : t('verification.notSpawned')}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refetchPanelStatus()}
                  disabled={panelStatusLoading}
                >
                  {t('verification.refresh')}
                </Button>
              </div>
              {panelStatus?.messageUrl && (
                <a
                  href={panelStatus.messageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline"
                >
                  {t('verification.openEmbed')}
                </a>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{t('verification.channel')}</Label>
                <Select
                  value={config.verificationChannelId || 'none'}
                  onValueChange={(value) => setConfig({ ...config, verificationChannelId: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('verification.selectChannel')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('verification.noneChannel')}</SelectItem>
                    {(channels as any[]).map((channel: any) => (
                      <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('verification.verifiedRole')}</Label>
                <Select
                  value={config.verifiedRoleId || 'none'}
                  onValueChange={(value) => setConfig({ ...config, verifiedRoleId: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('verification.selectRoleVerified')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('verification.noneRole')}</SelectItem>
                    {(roles as any[]).map((role: any) => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{t('verification.unverifiedRole')}</Label>
                <Select
                  value={config.unverifiedRoleId || 'none'}
                  onValueChange={(value) => setConfig({ ...config, unverifiedRoleId: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('verification.selectRoleUnverified')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('verification.noneRole')}</SelectItem>
                    {(roles as any[]).map((role: any) => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('verification.provider')}</Label>
                <Select value={config.captchaProvider} onValueChange={(value) => setConfig({ ...config, captchaProvider: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('verification.selectProvider')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discord-button">Discord Button Captcha</SelectItem>
                    <SelectItem value="hcaptcha">hCaptcha</SelectItem>
                    <SelectItem value="turnstile">Cloudflare Turnstile</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('verification.providerHelp')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{t('verification.maxAttempts')}</Label>
                <Input
                  type="number"
                  min="1"
                  value={config.maxAttempts}
                  onChange={(e) => setConfig({ ...config, maxAttempts: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <Label>{t('verification.timeoutMinutes')}</Label>
                <Input
                  type="number"
                  min="1"
                  value={config.timeoutMinutes}
                  onChange={(e) => setConfig({ ...config, timeoutMinutes: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>

            <div className="rounded-lg bg-white/5 p-4 space-y-3">
              <Label>{t('verification.methods')}</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm">{t('verification.methodButton')}</p>
                  <Switch
                    checked={config.methodButton}
                    onCheckedChange={(checked) => setConfig({ ...config, methodButton: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm">{t('verification.methodCaptcha')}</p>
                  <Switch
                    checked={config.methodCaptcha}
                    onCheckedChange={(checked) => setConfig({ ...config, methodCaptcha: checked, captchaEnabled: checked || config.captchaEnabled })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm">{t('verification.methodEmail')}</p>
                  <Switch
                    checked={config.methodEmail}
                    onCheckedChange={(checked) => setConfig({ ...config, methodEmail: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm">{t('verification.methodOAuth')}</p>
                  <Switch
                    checked={config.methodOAuthDiscord}
                    onCheckedChange={(checked) => setConfig({ ...config, methodOAuthDiscord: checked })}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                  <div>
                    <Label>{t('verification.captchaEnabled')}</Label>
                    <p className="text-xs text-muted-foreground">{t('verification.captchaEnabledDesc')}</p>
                  </div>
                  <Switch
                    checked={config.captchaEnabled}
                    onCheckedChange={(checked) => setConfig({ ...config, captchaEnabled: checked, methodCaptcha: checked || config.methodCaptcha })}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <Label>{t('verification.autoKick')}</Label>
                  <p className="text-xs text-muted-foreground">{t('verification.autoKickDesc')}</p>
                </div>
                <Switch
                  checked={config.autoKickUnverified}
                  onCheckedChange={(checked) => setConfig({ ...config, autoKickUnverified: checked })}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => spawnVerificationPanel()}
              disabled={spawningPanel || !config.verificationChannelId}
            >
              {spawningPanel ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t('verification.publishEmbed')}
            </Button>
            <Button onClick={() => updateConfig(config)} disabled={saving} className="min-w-[160px]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {t('verification.save')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export const Web3GatingPlugin = VerificationPlugin;
